import { useEffect, useCallback } from "react";
import { Configuration, Wall } from "../types/configuration";
import ConfiguratorService from "src/services/configuratorService";
import ConfigurationService from "src/services/configurationService";
import CalculationService from "src/services/calculationService";
import { addHouseWalls, removeHouseWalls } from "src/helpers/walls";
import { validateAndCleanWalls } from "src/helpers/wallValidation";
// @ts-ignore
import isEqual from "lodash/isEqual";
// @ts-ignore
import sortBy from "lodash/sortBy";
import { calculateKapschuurBigHeight } from "src/helpers/configuration";
import prices from "src/prices";

const sortWalls = (wall: Wall) => wall.id;

const useSynchronizeServices = (
  state: Configuration,
  isInitial: boolean,
  configuration: ConfigurationService,
  calculation: CalculationService,
  configurator: ConfiguratorService,
  setWalls: (walls: Configuration["walls"]) => void,
  setIsInitial: (isInitial: boolean) => void,
  setTotalPrice: (totalPrice: string) => void
) => {
  const synchronize = useCallback(() => {
    // Update configurator
    configurator.setState(state);

    const tmpDepth = state.dimensions.depth;
    let updatedWalls = state.walls;
    const currentWalls = configuration.getWalls();

    updatedWalls = configurator.updateCommonStoragesWalls(
      updatedWalls,
      currentWalls
    );

    if (state.roofType === "kapschuur") {
      state.dimensions.height = 3000;
      state.dimensions.overhangBack = 0;
      state.dimensions.overhangFront = 0;
      state.dimensions.overhangLeft = 0;
      state.dimensions.overhangRight = 0;
    }

    if (state.roofType === "platdak") {
      state.dimensions.height = 2200;
    }

    // Only reset roof.type when roofType actually changes, not on every sync
    const roofTypeChanged =
      !isInitial && configuration.getRoofType() !== state.roofType;

    if (isInitial || configuration.getRoofType() !== state.roofType) {
      state.dimensions.depth = 3000;

      if (state.roofType === "platdak") {
        state.dimensions.overhangFront = 150;
        if (roofTypeChanged) state.roof.type = "empty";
      }

      // if (state.roofType === "kapschuur") {
      //   if (roofTypeChanged) state.roof.type = "empty";
      // }
    }

    // Dimensions & storages
    const currentDimensions = configuration.getDimensions();
    const depthChanged = currentDimensions.depth !== state.dimensions.depth;
    const leftStorageWidthChanged =
      currentDimensions.leftStorageWidth !== state.dimensions.leftStorageWidth;
    const rightStorageWidthChanged =
      currentDimensions.rightStorageWidth !== state.dimensions.rightStorageWidth;
    const shouldValidateWall = (wall: Wall) => {
      if (wall.storage === "empty") return true;

      if (depthChanged && wall.id.startsWith("v-")) {
        return true;
      }

      if (
        rightStorageWidthChanged &&
        (wall.storage === "right_front" || wall.storage === "right_back")
      ) {
        return true;
      }

      if (
        leftStorageWidthChanged &&
        (wall.storage === "left_front" || wall.storage === "left_back")
      ) {
        return true;
      }

      return false;
    };

    if (isInitial || !isEqual(currentDimensions, state.dimensions)) {
      configurator.changeSize();
      configurator.changeStorageWidth();
    }

    // Roof type
    if (isInitial || configuration.getRoofType() !== state.roofType) {
      configurator.changeRoofType();
    }

    // Roof
    const currentRoof = configuration.getRoof();

    if (isInitial || !isEqual(currentRoof.type, state.roof.type)) {
      configurator.changeRoofMaterial();
    }

    // Options Material
    if (
      isInitial ||
      !isEqual(state.optionsMaterial, configuration.getOptionsMaterial())
    ) {
      const trim = state.optionsMaterial.daktrim_blank ? "metalic_ring" : "";
      configurator.changeRoofCustomization([trim]);
    }

    // Gutter
    const currentGutter = configuration.getGutter();

    if (isInitial || currentGutter.enabled !== state.gutter.enabled) {
      configurator.toggleGutter();
    }

    if (
      configuration.getRoofType() !== "platdak" &&
      state.roofType === "platdak"
    ) {
      state.gutter.enabled = false;
      configurator.toggleGutter();
    }

    if (
      state.gutter.enabled &&
      (isInitial || currentGutter.type !== state.gutter.type)
    ) {
      configurator.updateGutterMaterial();
    }

    // Walls and storages
    const prevStorages = configuration.getStorages();

    if (isInitial || prevStorages.left !== state.storages.left) {
      configurator.changeStorageWidth();

      if (state.storages.left) {
        updatedWalls = addHouseWalls(
          "left",
          configuration,
          configurator,
          updatedWalls
        );
      } else {
        updatedWalls = removeHouseWalls("left", updatedWalls);
        configurator.deselectWalls();
      }
    }

    if (isInitial || prevStorages.right !== state.storages.right) {
      configurator.changeStorageWidth();

      if (state.storages.right) {
        updatedWalls = addHouseWalls(
          "right",
          configuration,
          configurator,
          updatedWalls
        );
      } else {
        updatedWalls = removeHouseWalls("right", updatedWalls);
        configurator.deselectWalls();
      }
    }

    // Update walls if they changed

    // update right wall position
    updatedWalls = configurator.updateRightSideWallPositions(updatedWalls);

    // update right storage position
    updatedWalls = configurator.updateRightStoragePositions(updatedWalls);

    updatedWalls = validateAndCleanWalls(
      updatedWalls,
      configurator,
      state.roofType,
      shouldValidateWall
    );

    // update walls with door and window
    updatedWalls = configurator.updateDoorWindowWalls(
      updatedWalls,
      shouldValidateWall
    );

    // update sliding glass door
    updatedWalls = configurator.updateSlidingGlassWalls(updatedWalls);

    updatedWalls = updatedWalls.filter((wall: Wall) => {
      return (
        +wall.id[wall.id.length - 1] <= configurator.getColumnsBalks() / 2 - 1
      );
    });

    if (!isEqual(updatedWalls, state.walls)) {
      state.walls = updatedWalls;
    }

    state.walls = sortBy(state.walls, sortWalls).reverse();

    if (
      isInitial ||
      !isEqual(currentWalls, state.walls) ||
      !isEqual(currentDimensions, state.dimensions)
    ) {
      configurator.updateWalls();
    }

    configurator.updateColumnTypes();

    if (isInitial || configuration.getRoofType() !== state.roofType) {
      state.dimensions.depth = tmpDepth;
      configurator.changeSize();
      configurator.updateWalls();
    }

    // Calculate additional information
    state.params.kapschuurBigHeight = calculateKapschuurBigHeight(state);

    // Update ConfigurationService
    configuration.setState(state);

    // Update CalculationService
    calculation.setState(state);
    calculation.calculateConfiguration();
    setTotalPrice(calculation.getFormatTotalPrice());

    // //temp prices list
    // console.table(
    //   prices.map((p) => ({
    //     name: p.name,
    //     label: p.label,
    //     unit: p.unit,
    //     price: p.price,
    //     category: p.category,
    //   }))
    // );

    if (isInitial) {
      setIsInitial(false);
      configurator.resetCamera();
    }
  }, [
    calculation,
    configuration,
    configurator,
    isInitial,
    setIsInitial,
    setTotalPrice,
    state,
  ]);

  useEffect(() => {
    const interval = setInterval(() => {
      if (window.CONF && configurator.getColumnsBalks() > 0) {
        clearInterval(interval);
        synchronize();
      }
    }, 700);
  }, [configurator, state, synchronize]);
};

export default useSynchronizeServices;
